import {
  CurrencyCode,
  enumUnit,
  InventoryMovementStatus,
  InventoryMovementType,
  InventoryTransactionType,
  Unit,
} from '@rematter/frontend-lib-api-client';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export type MovementHistoryRecord = {
  id: string;
  itemId: string;
  itemName: string;
  itemSku: string;
  itemMeasurementUnit: string;
  facilityId: string;
  facilityName: string;
  fromAccountId: string;
  toAccountId: string;
  movementType: InventoryMovementType;
  originTransactionType: InventoryTransactionType;
  originTransactionItemId: string;
  occurredAt: string;
  quantity: number;
  quantityUnit: Unit;
  totalCost: {
    amount: number;
    currencyCode: CurrencyCode;
  } | null;
  calculatedMovementValue: {
    amount: number;
    currencyCode: CurrencyCode;
  } | null;
  status: InventoryMovementStatus;
  runningQuantity: number;
  runningQuantityUnit: Unit;
  runningValue: {
    amount: number;
    currencyCode: CurrencyCode;
  } | null;
  runningWac: {
    amount: number;
    currencyCode: CurrencyCode;
  } | null;
};

export const inventoryMovementHistoryQueryAtom = createQueryAtomFactory(
  'inventory.inventoryMovementHistory',
  (params: {
    itemId: string;
    facilityId?: string;
    currencyCode?: CurrencyCode;
    limit?: number;
    offset?: number;
  }) => ({
    inventoryMovementHistory: {
      __args: {
        filter: {
          itemId: params.itemId,
          facilityId: params.facilityId as string,
          currencyCode: params.currencyCode,
        },
        pagination: {
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        movement: {
          id: true,
          itemId: true,
          item: {
            id: true,
            name: true,
            skuNumber: true,
            measurementUnit: true,
          },
          facilityId: true,
          facility: {
            id: true,
            facilityName: true,
          },
          fromAccountId: true,
          toAccountId: true,
          movementType: true,
          originTransactionType: true,
          originTransactionItemId: true,
          occurredAt: true,
          quantity: {
            value: true,
            unit: true,
          },
          totalCost: {
            amount: true,
            currencyCode: true,
          },
          status: true,
        },
        runningTotals: {
          runningQuantity: {
            value: true,
            unit: true,
          },
          runningValue: {
            amount: true,
            currencyCode: true,
          },
          runningWeightedAverageCost: {
            amount: true,
            currencyCode: true,
          },
          calculatedMovementValue: {
            amount: true,
            currencyCode: true,
          },
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      movements: MovementHistoryRecord[];
      totalCount: number;
    } => {
      if (
        !data ||
        !data.inventoryMovementHistory ||
        !data.inventoryMovementHistory.entries
      ) {
        return {
          movements: [],
          totalCount: 0,
        };
      }

      return {
        movements: data.inventoryMovementHistory.entries.map((entry) => {
          const movement = entry.movement;
          const runningTotals = entry.runningTotals;
          return {
            id: movement.id,
            itemId: movement.itemId,
            itemName: movement.item?.name || '',
            itemSku: movement.item?.skuNumber || '',
            itemMeasurementUnit: movement.item?.measurementUnit || 'Pounds',
            facilityId: movement.facilityId,
            facilityName: movement.facility?.facilityName || '',
            fromAccountId: movement.fromAccountId,
            toAccountId: movement.toAccountId,
            movementType: movement.movementType,
            originTransactionType: movement.originTransactionType,
            originTransactionItemId: movement.originTransactionItemId,
            occurredAt: movement.occurredAt,
            quantity: Number(movement.quantity?.value) || 0,
            quantityUnit: movement.quantity?.unit || enumUnit.Pounds,
            totalCost: movement.totalCost
              ? {
                  amount: Number(movement.totalCost.amount) || 0,
                  currencyCode: movement.totalCost.currencyCode,
                }
              : null,
            calculatedMovementValue: runningTotals.calculatedMovementValue
              ? {
                  amount:
                    Number(runningTotals.calculatedMovementValue.amount) || 0,
                  currencyCode:
                    runningTotals.calculatedMovementValue.currencyCode,
                }
              : null,
            status: movement.status,
            runningQuantity: Number(runningTotals.runningQuantity?.value) || 0,
            runningQuantityUnit:
              (movement.item?.measurementUnit as Unit) || enumUnit.Pounds,
            runningValue: runningTotals.runningValue
              ? {
                  amount: Number(runningTotals.runningValue.amount) || 0,
                  currencyCode: runningTotals.runningValue.currencyCode,
                }
              : null,
            runningWac: runningTotals.runningWeightedAverageCost
              ? {
                  amount:
                    Number(runningTotals.runningWeightedAverageCost.amount) ||
                    0,
                  currencyCode:
                    runningTotals.runningWeightedAverageCost.currencyCode,
                }
              : null,
          };
        }),
        totalCount: data.inventoryMovementHistory.page?.total || 0,
      };
    },
  },
);
